import { z } from 'zod';
import { convertToBoolean } from '../utils/boolean';

export const booleanStrings = ['true', 'false'] as const;
export const requiredString = z.string().min(1);

/**
 * Adapted from zod version 3.23.8
 * https://github.com/colinhacks/zod/blob/main/src/types.ts#L599
 * */
export const emailRegex =
  /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/;
export const emailRegexString = emailRegex.toString().slice(1, -1);

export const PROFESSIONAL_INTEREST_MAX_COUNT = 20;
export const PROFESSIONAL_INTEREST_MAX_LENGTH = 200;

export const EMAIL_MAX_LENGTH = 128;
export const EMAIL_MAX_LENGTH_MESSAGE =
  'Email address cannot exceed 128 characters.';

export const isValidEmail = (email: string) => {
  return emailRegex.test(email);
};

export const baseEmailValidator = z
  .string()
  .max(EMAIL_MAX_LENGTH, EMAIL_MAX_LENGTH_MESSAGE)
  .transform(val => val.toLowerCase())
  .refine(isValidEmail, {
    message: 'Invalid email address'
  });
export const requiredEmailValidator = z
  .string()
  .min(1, 'Email is required')
  .pipe(baseEmailValidator);

const fileSchema = z.object(
  {
    name: z.string(),
    extension: z.string(),
    url: z.string()
  },
  {
    required_error: 'Select file to continue'
  }
);

export const keywordsFormSchema = z
  .array(
    z.object({
      text: z.string()
    })
  )
  .min(1, 'Keywords are required');

export const authorSchema = z.object({
  firstName: z.string().min(1, 'Given name is required'),
  lastName: z.string().min(1, 'Family name is required'),
  email: z.string().min(1, 'Email is required'),
  affiliation: z.string().min(1, 'Affiliation is required')
});

export const submittingAuthorSchema = authorSchema.merge(
  z.object({
    email: requiredEmailValidator,
    orcid: z.string().refine(value => orcidIdOrUrlRegex.test(value), {
      message: 'Invalid ORCID format'
    })
  })
);
const publishingModel = z.object({
  model: z.enum(['subscription', 'openAccess'], {
    invalid_type_error: 'Publishing preference is required'
  })
});

export const publishingModelSchema = z.object({
  publishingModel: publishingModel
});

export const transparentPeerReviewSchema = z
  .boolean({
    invalid_type_error:
      'Indicate your preference for transparent peer review before proceeding.'
  })
  .or(z.enum(booleanStrings))
  .transform(value => convertToBoolean(value));

export const doubleAnonymousPeerReviewSchema = z.boolean({
  invalid_type_error:
    'Indicate your preference for double anonymous peer review before proceeding.'
});

export const editPreferenceSchema = z.object({
  immediatePublication: z
    .boolean({
      invalid_type_error:
        'Indicate your preference for immediate publication before proceeding.'
    })
    .or(z.enum(booleanStrings))
    .transform(value => convertToBoolean(value))
    .optional(),
  transparentPeerReview: transparentPeerReviewSchema.optional(),
  doubleAnonymousPeerReview: doubleAnonymousPeerReviewSchema.optional(),
  publishingModel: publishingModel.optional()
});

export const preferenceAndFileSetSchema = z.object({
  preference: z.object({
    dataAvailability: z
      .boolean({
        invalid_type_error:
          'Indicate your preference for data availability before proceeding.'
      })
      .or(z.enum(booleanStrings))
      .transform(value => convertToBoolean(value)),
    dataUnavailabilityReason: z.string().or(z.literal(null)),
    immediatePublication: z
      .boolean({
        invalid_type_error:
          'Indicate your preference for immediate publication before proceeding.'
      })
      .or(z.enum(booleanStrings))
      .transform(value => convertToBoolean(value)),
    transparentPeerReview: transparentPeerReviewSchema,
    doubleAnonymousPeerReview: z
      .boolean({
        invalid_type_error:
          'Indicate your preference for double anonymous peer review before proceeding.'
      })
      .or(z.enum(booleanStrings))
      .transform(value => convertToBoolean(value)),
    publishingModel: publishingModel,
    acknowledgePublicationCharge: z.boolean().optional()
  }),
  fileSet: z.object({
    Manuscript: fileSchema,
    AnonymizedManuscript: fileSchema.optional(),
    Supplementary: z.array(fileSchema).optional(),
    CoverLetter: fileSchema.optional()
  })
});

export const changeRequestSubmissionSchema = z.object({
  fields: z.array(z.object({ value: z.string() })),
  reason: z.string().min(1, 'Response to Author is required'),
  attachments: z.array(fileSchema).optional()
});

export const submissionMetaDataSchema = z.object({
  title: z.string().min(1).max(1000),
  abstract: z.string().min(1).max(10000),
  keywords: z.string().array().min(1, 'Keywords are required'),
  needsEthicsStatement: z
    .boolean({
      invalid_type_error: 'Select an option'
    })
    .or(z.enum(booleanStrings))
    .transform(value => convertToBoolean(value)),
  ethicsStatement: z
    .string()
    .max(2000, 'Shorten your ethics statement to less than 2000 characters'),
  noveltyStatement: z
    .string()
    .min(2, 'Novelty statement is required')
    .max(2000, 'Shorten your statement to less than 2000 characters'),
  hasFunding: z
    .boolean({
      invalid_type_error:
        'Please indicate if there is funding to report for this submission.'
    })
    .or(z.enum(booleanStrings))
    .transform(value => convertToBoolean(value)),
  funding: z.array(
    z.object({
      fundingBody: z.string().min(1),
      grantNumber: z.string().optional()
    })
  ),
  hasReproducedMaterial: z
    .boolean({
      invalid_type_error:
        'Please indicate whether your submission includes previously published material to proceed.'
    })
    .or(z.enum(['true', 'false']))
    .transform(value => convertToBoolean(value)),
  reproducedMaterials: z.array(
    z.object({
      name: z.string().min(1)
    })
  )
});

export const notesContentSchema = z.object({
  title: z.string().trim().min(1, 'Please enter a Title.'),
  category: z
    .enum(['General', 'Permission', 'Assessment', 'Decision'])
    .optional(),
  note: z
    .string()
    .trim()
    .max(1000, 'Note cannot exceed 1000 characters.')
    .optional(),
  mentions: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        email: z.string(),
        type: z.enum(['user', 'journal']),
        shortname: z.string().optional(),
        focusIssues: z
          .array(
            z.object({
              id: z.string(),
              name: z.string(),
              issueId: z.string(),
              issueLink: z.string().optional(),
              status: z.string()
            })
          )
          .optional(),
        description: z.string().optional(),
        organizationId: z.string().optional(),
        introduction: z.string().optional()
      })
    )
    .optional(),
  attachments: z
    .array(
      z.object({
        id: z.string().optional(),
        referenceName: z.string(),
        name: z.string(),
        extension: z.string(),
        url: z.string(),
        visibility: z.string().optional(),
        errors: z.array(z.string()).optional(),
        warnings: z.array(z.string()).optional()
      })
    )
    .optional()
});

export const notesValidationSchema = z.object({
  submissionContextId: z.string().min(1),
  submissionId: z.string().min(1),
  targetTag: z.string().optional(),
  parentNoteId: z.string().optional(),
  checklistCategory: z.string().optional()
});

export const workspaceArticleTypeValidator = z.object({
  name: z.string().trim().min(1, 'Name is required')
});

export const rateReviewSchema = z.object({
  rating: z.string().min(1, 'Please select a rating'),
  justification: z
    .string()
    .max(1000, 'Maximum of 1000 characters allowed')
    .optional()
});

export const prepareResponseSchema = z.object({
  editorComment: z
    .string()
    .trim()
    .min(1, 'Please enter a response to the authors')
});

export const rescindReviewSchema = z.object({
  feedback: z.string().min(1, 'Please enter a response to the authors'),
  attachments: z.array(fileSchema).optional()
});

export const declineReviewSchema = z.object({
  declineReason: z.string(),
  declineReasonList: z
    .string()
    .min(1, 'Please enter a response to the editor.'),
  recommendReviewers: z.boolean({
    invalid_type_error:
      'Please indicate whether you would like to recommend specific reviewers for this Submission?'
  }),
  recommendedReviewers: z.array(
    z.object({
      email: requiredEmailValidator
    })
  )
});

export const inviteReviewerSchema = z.object({
  reviewers: z.array(
    z.object({
      email: requiredEmailValidator
    })
  )
});

export const inviteExpertsSchema = z
  .object({
    experts: z.array(
      z.object({
        id: z.string().min(1, 'Please search for an Expert.'),
        name: z.string().optional(),
        email: z.string().optional()
      })
    ),
    addContext: z.object({
      comment: z.string().trim().min(1, 'Please enter a Task to Perform.'),
      attachments: z.array(fileSchema).optional()
    })
  })
  .superRefine((data, ctx) => {
    const seen = new Set<string>();
    data.experts.forEach((expert, index) => {
      if (seen.has(expert.id)) {
        ctx.addIssue({
          path: ['experts', index, 'id'], // precise error at the duplicate index
          message: 'Duplicate Expert, Please search for another Expert.',
          code: z.ZodIssueCode.custom
        });
      } else {
        seen.add(expert.id);
      }
    });
  });
export const inviteJournalBoardMembersSchema = z.object({
  boardMembers: z.array(
    z.object({
      email: z.string().min(1, 'Email is required').refine(isValidEmail, {
        message: 'Invalid email address'
      })
    })
  )
});

export const inviteReviewerBulkUploadSchema = z.object({
  emails: z
    .string()
    .min(1, 'Please enter at least one email address.')
    .refine(
      emails => {
        const lines = emails.split('\n');
        const trimmedLines = lines.map(line => line.trim());
        return trimmedLines.every(isValidEmail);
      },
      {
        message:
          'Some emails have formatting errors. Review and correct these so that all emails follow the format yourname@example.com in individual lines with no commas.'
      }
    )
    .refine(
      emails => {
        const lines = emails.split('\n');
        const trimmedLines = lines.map(line => line.trim());
        const uniqueEmails = new Set(trimmedLines);

        return uniqueEmails.size === trimmedLines.length;
      },
      {
        message:
          'Some emails are duplicates. Please remove any repeated addresses.'
      }
    )
});
export const inviteAdjudicatorsSchema = z.object({
  experts: z
    .array(
      z
        .object({
          id: z.string().optional(),
          name: z.string().optional(),
          email: z.string().optional(),
          externalExpertEmail: z
            .string()
            .optional()
            .refine(val => !val || isValidEmail(val), {
              message: 'Invalid email address'
            })
        })
        .superRefine((data, ctx) => {
          const hasId = !!data.id?.trim();
          const hasExternal = !!data.externalExpertEmail?.trim();

          if (!hasId && !hasExternal) {
            // Neither provided – show both errors
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Please search for an Expert.',
              path: ['id']
            });
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Please enter an External Expert Email.',
              path: ['externalExpertEmail']
            });
          }
        })
    )
    .superRefine((experts, ctx) => {
      const seenEmails = new Map<string, number[]>();

      experts.forEach((expert, index) => {
        const email = (expert.email || expert.externalExpertEmail || '')
          .trim()
          .toLowerCase();
        if (!email) return;

        if (!seenEmails.has(email)) {
          seenEmails.set(email, [index]);
        } else {
          seenEmails.get(email)!.push(index);
        }
      });

      for (const indices of seenEmails.values()) {
        if (indices.length > 1) {
          indices.forEach(i => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Duplicate Expert, Please search for another Expert.',
              path: [i, 'email'] // shows on the email field
            });
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Duplicate Expert, Please search for another Expert.',
              path: [i, 'externalExpertEmail'] // also show on external field just in case
            });
          });
        }
      }
    }),
  addContext: z.object({
    comment: z.string().trim().min(1, 'Please enter a Task to Perform.'),
    attachments: z.array(fileSchema).optional()
  })
});

export const organizationTagValidator = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Name is required')
      .max(100, 'Max 100 characters are allowed'),
    description: z
      .string()
      .trim()
      .min(1, 'Description is required')
      .max(100, 'Max 100 characters are allowed'),
    usage: z.enum(['submissions', 'users'], {
      message: 'Please select a usage'
    }),
    // Always required
    tagCategoryId: z.string().trim().min(1, 'Tag category is required'),
    scope: z.enum(['global', 'journal']).optional(),
    submissionContextId: z.string().optional()
  })
  // Required only when usage = submissions
  .refine(
    data => {
      if (data.usage === 'submissions') {
        return !!data.submissionContextId;
      }
      return true;
    },
    {
      message: 'Journal context is required for submission tags',
      path: ['submissionContextId']
    }
  )
  // Journal-scoped user tags require a journal context
  .refine(data => data.scope !== 'journal' || !!data.submissionContextId, {
    message: 'Journal context is required for journal-scoped tags',
    path: ['submissionContextId']
  })
  // Global user tags must not carry a journal context
  .refine(data => data.scope !== 'global' || !data.submissionContextId, {
    message: 'Journal context must not be set for global tags',
    path: ['submissionContextId']
  });

export const workspaceCategoryValidator = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(100, 'Max 100 characters are allowed'),
  description: z
    .string()
    .trim()
    .max(200, 'Max 200 characters are allowed')
    .optional(),
  color: z
    .string()
    .min(1, 'Color is required')
    .regex(/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/, 'Invalid hex color')
});

export const editorialRequestCopyrightSchema = z.object({
  responseToAuthor: z.string().min(1, 'Please enter a response to the authors'),
  attachments: z.array(fileSchema).optional()
});

export const declineTransferValidator = z.object({
  declineReason: z
    .string()
    .min(1, 'Please enter a Reason for Declining.')
    .max(3000)
});

export const inviteUsersSchema = z.object({
  users: z.array(
    z.object({
      email: z
        .string()
        .min(1, 'Please enter an Email.')
        .max(EMAIL_MAX_LENGTH, EMAIL_MAX_LENGTH_MESSAGE)
        .refine(isValidEmail, {
          message: 'Invalid email address'
        })
    })
  )
});

type RecipientField = 'to' | 'cc' | 'bcc';

const tagArraySchema = z.array(
  z.object({
    text: z.string().min(1)
  })
);

const fieldLabels: Record<string, string> = {
  to: 'To',
  cc: 'Cc',
  bcc: 'Bcc'
};

export const emailContentSchema = z.object({
  emailContent: z.object({
    subject: z.string().min(1, 'Subject is required'),
    body: z.string().min(1, 'Body text is required'),
    recipients: z
      .object({
        to: tagArraySchema.min(1, 'To is required'),
        cc: tagArraySchema.optional(),
        bcc: tagArraySchema.optional()
      })
      .superRefine((recipients, ctx) => {
        const normalize = (list: { text: string }[]) =>
          list.map(i => i.text.trim().toLowerCase());

        const groups: Record<string, RecipientField> = {};

        // Iterate dynamically instead of 3 manual calls
        (['to', 'cc', 'bcc'] as RecipientField[]).forEach(field => {
          const emails = normalize(recipients[field] ?? []);
          emails.forEach(email => {
            if (groups[email]) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: `Email already exists in ${fieldLabels[groups[email]]}`
              });
            } else {
              groups[email] = field;
            }
          });
        });
      }),
    attachments: z
      .array(
        z.object({
          id: z.string().optional(),
          referenceName: z.string(),
          name: z.string(),
          extension: z.string(),
          url: z.string(),
          visibility: z.string().optional(),
          errors: z.array(z.string()).optional(),
          warnings: z.array(z.string()).optional()
        })
      )
      .optional()
  })
});

export const revertDecisionValidator = z.object({
  reason: z
    .string()
    .min(1, 'Please enter a reason for reversal to proceed.')
    .refine(
      value => value.trim().length > 0,
      'Reason for Reversal cannot be empty'
    )
});

export const orcidIdRegex = /^\d{4}-\d{4}-\d{4}-\d{3}[0-9X]$/;
export const orcidUrlRegex =
  /^https:\/\/orcid.org\/\d{4}-\d{4}-\d{4}-\d{3}[0-9X]$/;

export const orcidIdOrUrlRegex = new RegExp(
  `(${orcidIdRegex.source}|${orcidUrlRegex.source})`
);

/**
 * Truncate a text to `wordsLimit` words, adding an ellipsis if it is longer.
 * @param {string} text
 * @param {number} [wordsLimit=65]
 * @returns {string}
 */
export const truncateWords = (text: string, wordsLimit = 65) => {
  const words = text.trim().split(/\s+/);
  if (words.length <= wordsLimit) return text.trim();
  return words.slice(0, wordsLimit).join(' ') + '…';
};

export * from './role';
export * from './submissions';
export * from './saved-filters';
export * from './early-assessment';
export * from './adjudication';
export * from './email-service';
export * from './deadline';
